import { apiClient, HttpError } from '@littlebird/auth-client';
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useMemo } from 'react';
import { z } from 'zod';

export const USER_SKILLS_ROOT = '/mnt/skills';
export const SYSTEM_SKILLS_ROOT = '/mnt/system-skills';
export const TEAM_SKILLS_ROOT = '/mnt/team-skills';
export const SKILL_FILE_NAME = 'SKILL.md';

/** Mirrors the backend rules in skills_frontmatter.py so the obvious case fails before a round-trip */
export const SKILL_NAME_RE = /^[a-z0-9]+(-[a-z0-9]+)*$/;
export const MAX_SPEC_SKILL_NAME_CHARS = 64;
export const MAX_SKILL_DESCRIPTION_CHARS = 500;
export const MAX_SKILL_BODY_CHARS = 100_000;

const VfsSkillSchema = z.object({
  name: z.string().nullable(),
  description: z.string().nullable(),
  /** Open string, not an enum: the backend may add problem kinds and the UI only checks for null */
  problem: z.string().nullable(),
});

const TeamSkillSchema = z.object({
  id: z.string(),
  shared_by: z.string().nullable(),
  provisioned: z.boolean(),
  enabled: z.boolean(),
});

const TeamShareSchema = z.object({
  id: z.string(),
  is_stale: z.boolean(),
});

const VfsCatalogueEntrySchema = z.object({
  path: z.string(),
  kind: z.enum(['file', 'directory']),
  size_bytes: z.number(),
  updated_at: z.string().nullable(),
  /** Present only on top-level skill directories of a skills root */
  skill: VfsSkillSchema.nullable(),
  team_skill: TeamSkillSchema.nullish(),
  team_share: TeamShareSchema.nullish(),
});

export type VfsCatalogueEntry = z.infer<typeof VfsCatalogueEntrySchema>;
type VfsSkill = z.infer<typeof VfsSkillSchema>;
export type TeamSkill = z.infer<typeof TeamSkillSchema>;
export type TeamShare = z.infer<typeof TeamShareSchema>;

const VfsFileSchema = z.object({
  path: z.string(),
  text: z.string(),
  size_bytes: z.number(),
  updated_at: z.string().nullable(),
  truncated: z.boolean(),
});

export class VfsFileNotFoundError extends Error {}

const retryTransientVfsErrors = (failureCount: number, error: unknown) =>
  failureCount < 3 &&
  !(error instanceof VfsFileNotFoundError) &&
  !(error instanceof z.ZodError);

export interface Skill {
  /** Always the directory name, matching what the agent's prompt shows and what `/name` inserts */
  name: string;
  /** Display name: the slug in title case, so "meeting-prep" reads "Meeting Prep" */
  label: string;
  description: string;
  /** Absolute VFS path of the skill directory */
  dir: string;
  /** Absolute VFS path of the SKILL.md, used to fetch the full content */
  path: string;
  isSystem: boolean;
  isTeam: boolean;
  teamSkill: TeamSkill | null;
  teamShare: TeamShare | null;
  isBroken: boolean;
  problem: string | null;
  /** Absolute paths of every file under the skill directory other than its SKILL.md */
  files: string[];
}

const PLUGINS_KEYS = {
  all: ['plugins'] as const,
  catalogue: ['plugins', 'catalogue'] as const,
  file: (path: string) => [...PLUGINS_KEYS.all, 'file', path] as const,
};

const CATALOGUE_STALE_TIME_MS = 15_000;

const SKILLS_ROOTS = [USER_SKILLS_ROOT, TEAM_SKILLS_ROOT, SYSTEM_SKILLS_ROOT];

export const getVfsCatalogueQuery = () =>
  queryOptions({
    queryKey: PLUGINS_KEYS.catalogue,
    staleTime: CATALOGUE_STALE_TIME_MS,
    refetchOnMount: true,
    retry: retryTransientVfsErrors,
    queryFn: async ({ signal }) => {
      const rootsParams = new URLSearchParams(
        SKILLS_ROOTS.map((root) => ['roots', root])
      );
      try {
        const data = await apiClient.get(`vfs/catalogue?${rootsParams}`, {
          signal,
        });
        return z.array(VfsCatalogueEntrySchema).parse(data);
      } catch (error) {
        throw error instanceof HttpError
          ? new Error('Failed to fetch skills')
          : error;
      }
    },
  });

export const getVfsFileQuery = (path: string | null) =>
  queryOptions({
    queryKey: PLUGINS_KEYS.file(path ?? ''),
    enabled: !!path,
    staleTime: 0,
    refetchOnMount: 'always',
    retry: retryTransientVfsErrors,
    queryFn: async ({ signal }) => {
      if (!path) {
        throw new Error('No file path selected');
      }
      try {
        const data = await apiClient.get(
          `vfs/file?path=${encodeURIComponent(path)}`,
          { signal }
        );
        return VfsFileSchema.parse(data);
      } catch (error) {
        if (error instanceof HttpError && error.status === 404) {
          throw new VfsFileNotFoundError('File not found');
        }
        throw error instanceof HttpError
          ? new Error('Failed to fetch skill')
          : error;
      }
    },
  });

const titleCaseSlug = (slug: string): string =>
  slug
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

/** A skill is a top-level directory of its root; the API marks those with a `skill` field */
export function toSkills(entries: VfsCatalogueEntry[]): Skill[] {
  const filePaths = entries
    .filter((entry) => entry.kind === 'file')
    .map((entry) => entry.path);
  return entries
    .filter(
      (entry): entry is VfsCatalogueEntry & { skill: VfsSkill } =>
        entry.kind === 'directory' && entry.skill !== null
    )
    .map((entry) => {
      const skillFilePath = `${entry.path}/${SKILL_FILE_NAME}`;
      const dirPrefix = `${entry.path}/`;
      // Directory name, not frontmatter name: the agent's prompt addresses
      // skills by directory, so the page must tell the same story
      const name = entry.path.split('/').at(-1) ?? '';
      return {
        name,
        label: titleCaseSlug(name),
        description: entry.skill.description ?? '',
        dir: entry.path,
        path: skillFilePath,
        isSystem: entry.path.startsWith(`${SYSTEM_SKILLS_ROOT}/`),
        isTeam: entry.path.startsWith(`${TEAM_SKILLS_ROOT}/`),
        teamSkill: entry.team_skill ?? null,
        teamShare: entry.team_share ?? null,
        isBroken: entry.skill.problem !== null,
        problem: entry.skill.problem,
        files: filePaths
          .filter(
            (path) => path.startsWith(dirPrefix) && path !== skillFilePath
          )
          .sort(),
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** Split a SKILL.md into its raw frontmatter block and the body, mirroring the backend's parse rules */
export function splitFrontmatter(text: string): {
  frontmatter: string | null;
  body: string;
} {
  if (!text.startsWith('---\n')) {
    return { frontmatter: null, body: text };
  }
  const separatorIndex = text.indexOf('\n---\n', 4);
  if (separatorIndex === -1) {
    return { frontmatter: null, body: text };
  }
  return {
    frontmatter: text.slice(4, separatorIndex),
    body: text.slice(separatorIndex + 5).replace(/^\n+/, ''),
  };
}

export function filterSkills(skills: Skill[], query: string): Skill[] {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) {
    return skills;
  }
  return skills.filter((skill) =>
    `${skill.name} ${skill.label} ${skill.description}`
      .toLowerCase()
      .includes(normalizedQuery)
  );
}

export const trySkillPrompt = (skill: Skill) =>
  `Try ${skill.isSystem ? 'the built-in' : 'my'} "${skill.name}" skill. Run through it now.`;

export const CREATE_SKILL_PROMPT =
  'Help me create a new skill. Ask me what it should do and when you should use it, then set it up.';

export function useSkillsQuery() {
  const catalogue = useQuery(getVfsCatalogueQuery());
  const { systemSkills, teamSkills, userSkills } = useMemo(() => {
    const skills = toSkills(catalogue.data ?? []);
    return {
      systemSkills: skills.filter((skill) => skill.isSystem),
      teamSkills: skills.filter((skill) => skill.isTeam),
      userSkills: skills.filter((skill) => !skill.isSystem && !skill.isTeam),
    };
  }, [catalogue.data]);

  return {
    systemSkills,
    teamSkills,
    userSkills,
    isLoading: catalogue.isLoading,
    error: catalogue.error,
  };
}

export interface SkillForm {
  name: string;
  description: string;
  body: string;
}

export function hydrateSkillForm(
  skill: Skill | undefined,
  fileText: string | undefined
): SkillForm {
  if (!skill) {
    return { name: '', description: '', body: '' };
  }
  return {
    name: skill.name,
    description: skill.description,
    body: fileText === undefined ? '' : splitFrontmatter(fileText).body,
  };
}

/**
 * The same judgment the backend makes, so the form can gate Save without a round-trip.
 * An empty name yields no message: the field is required, not wrong.
 */
export function validateSkillForm(
  form: SkillForm,
  taken?: Skill
): Partial<Record<keyof SkillForm, string>> {
  const errors: Partial<Record<keyof SkillForm, string>> = {};

  if (
    form.name &&
    (!SKILL_NAME_RE.test(form.name) ||
      form.name.length > MAX_SPEC_SKILL_NAME_CHARS)
  ) {
    errors.name = `Use lowercase letters, numbers and dashes, up to ${MAX_SPEC_SKILL_NAME_CHARS} characters.`;
  } else if (taken) {
    errors.name = taken.isSystem
      ? `Littlebird already has a built-in skill called "${taken.name}".`
      : `You already have a skill called "${taken.name}".`;
  }

  if (!form.description.trim()) {
    errors.description = 'Say when Littlebird should use this skill.';
  } else if (form.description.length > MAX_SKILL_DESCRIPTION_CHARS) {
    errors.description = `Keep this under ${MAX_SKILL_DESCRIPTION_CHARS} characters.`;
  }

  if (!form.body.trim()) {
    errors.body = 'Add the steps Littlebird should follow.';
  } else if (form.body.length > MAX_SKILL_BODY_CHARS) {
    errors.body = `Keep the instructions under ${MAX_SKILL_BODY_CHARS.toLocaleString()} characters.`;
  }

  return errors;
}

/** Which field a server rejection belongs under. Unknown kinds fall back to a toast. */
export const SKILL_WRITE_PROBLEM_FIELD: Record<string, keyof SkillForm> = {
  invalid_name: 'name',
  name_taken: 'name',
  empty_description: 'description',
  description_too_long: 'description',
  empty_body: 'body',
  body_too_long: 'body',
};

const SkillWriteRejectionSchema = z.object({
  detail: z.object({ problem: z.string(), message: z.string() }),
});

/** A 422 from the skill write endpoints: the server refused and says which field is wrong */
export class SkillWriteError extends Error {
  constructor(
    public readonly problem: string,
    message: string
  ) {
    super(message);
  }
}

const toSkillWriteError = (error: unknown) => {
  if (error instanceof HttpError && error.status === 422) {
    const parsed = SkillWriteRejectionSchema.safeParse(error.body);
    if (parsed.success) {
      return new SkillWriteError(
        parsed.data.detail.problem,
        parsed.data.detail.message
      );
    }
  }
  return error;
};

export function usePutSkillMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      name,
      description,
      body,
      create,
      currentName,
    }: SkillForm & {
      create?: boolean;
      /** Edit mode: the saved name; the server renames the skill when `name` differs */
      currentName?: string;
    }) => {
      try {
        const data = await apiClient.put(
          `vfs/skills/${encodeURIComponent(currentName ?? name)}`,
          {
            description,
            body,
            create,
            new_name: currentName === undefined ? undefined : name,
          }
        );
        return VfsCatalogueEntrySchema.parse(data);
      } catch (error) {
        throw toSkillWriteError(error);
      }
    },
    // Returned so TanStack settles the refetch before the call-site onSuccess runs
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: PLUGINS_KEYS.all }),
  });
}

const SkillRefinementSchema = z.object({
  description: z.string(),
  body: z.string(),
});

/** Rewrites a draft without saving it; the form decides whether to keep the result */
export function useRefineSkillMutation() {
  return useMutation({
    mutationFn: async ({ name, description, body }: SkillForm) => {
      try {
        const data = await apiClient.post(
          `vfs/skills/${encodeURIComponent(name)}/refine`,
          { description, body }
        );
        return SkillRefinementSchema.parse(data);
      } catch (error) {
        throw toSkillWriteError(error);
      }
    },
  });
}

export function useDeleteSkillMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (name: string) => {
      try {
        await apiClient.delete(`vfs/skills/${encodeURIComponent(name)}`);
      } catch (error) {
        throw toSkillWriteError(error);
      }
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: PLUGINS_KEYS.all }),
  });
}

const invalidateOnSuccess = (
  queryClient: ReturnType<typeof useQueryClient>
) => ({
  onSuccess: () =>
    queryClient.invalidateQueries({ queryKey: PLUGINS_KEYS.all }),
});

export function useShareSkillMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (name: string) => {
      const data = await apiClient.post(
        `vfs/skills/${encodeURIComponent(name)}/share_with_team`
      );
      return z.object({ team_share: TeamShareSchema }).parse(data).team_share;
    },
    ...invalidateOnSuccess(queryClient),
  });
}

export function useRemoveTeamSkillMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`team_skills/${id}`),
    ...invalidateOnSuccess(queryClient),
  });
}

export function useProvisionTeamSkillMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, provisioned }: { id: string; provisioned: boolean }) =>
      apiClient.patch(`team_skills/${id}/provision`, { provisioned }),
    ...invalidateOnSuccess(queryClient),
  });
}

export function useSetTeamSkillStateMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      apiClient.put(`team_skills/${id}/state`, { enabled }),
    ...invalidateOnSuccess(queryClient),
  });
}
